import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"

import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

import {
  fetchSucursalById,
  fetchServiciosBySucursalId,
  updateServicios,
  fetchAllServicios,
  fetchAllWatchlist,
} from "./service"

import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"
import { showToast } from "utils/swalToast"

function AddServiciosSucursal() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [selectRows, setSelectRows] = useState([])
  const [servicios, setServicios] = useState([])
  const [watchlist, setWatchlist] = useState([])
  const [deletedServicios, setDeletedServicios] = useState([])
  const [current, setCurrent] = useState()

  const selectedIds = selectRows.map((r) => r.selectedServicio).filter((v) => v !== "" && v != null)
  const uniqueSelected = new Set(selectedIds)
  const remainingCount = Math.max(0, servicios.length - uniqueSelected.size)

  const getServicioOptions = (rowIndex) => {
    const currentValue = selectRows[rowIndex]?.selectedServicio
    const takenByOtherRows = new Set(
      selectRows
        .filter((_, idx) => idx !== rowIndex)
        .map((r) => r.selectedServicio)
        .filter((v) => v !== "" && v != null)
    )
    return servicios.filter(
      (s) => !takenByOtherRows.has(s.idServicio) || s.idServicio === currentValue
    )
  }

  const disableAdd = remainingCount === 0

  const handleAddRow = () => {
    if (disableAdd) return
    setSelectRows([
      ...selectRows,
      { selectedServicio: "", selectedWatchlistClientes: "", selectedWatchlistInvitados: "" },
    ])
  }

  useEffect(() => {
    fetchAllServicios().then(setServicios)
    fetchAllWatchlist().then(setWatchlist)
    if (id) {
      fetchSucursalById(id).then((sucursal) => setCurrent(sucursal))
      fetchServiciosBySucursalId(id).then((response) => {
        const serviciosActuales =
          response?.sucursalesServicios || response?.sucursalServicios || response
        if (Array.isArray(serviciosActuales)) {
          setSelectRows(serviciosActuales)
        }
      })
    }
  }, [id])

  const handleSelectChange = (index, name, value) => {
    const updatedRows = selectRows.map((row, idx) =>
      idx === index ? { ...row, [name]: value } : row
    )
    setSelectRows(updatedRows)
  }

  const handleRemoveRow = (index) => {
    setDeletedServicios([...deletedServicios, selectRows[index].selectedServicio])
    setSelectRows(selectRows.filter((_, idx) => idx !== index))
  }

  const handleSave = async () => {
    try {
      await updateServicios(id, {
        sucursalesServicios: selectRows,
        deletedServicios,
      })
      showToast({ title: "Servicios guardados", icon: "success" })
      navigate("/sucursales")
    } catch (error) {
      console.error(error)
      showToast({ title: "Error", text: "No se pudieron guardar los servicios", icon: "error" })
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={10}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h5">
                  {current?.descripcion}: Agregar/Editar Servicios
                </MDTypography>
              </MDBox>
              <MDBox p={2}>
                {selectRows.map((row, index) => (
                  <Grid container spacing={2} mt={0.5} key={index} alignItems="center">
                    <Grid item xs={12} sm={5} md={5}>
                      <FormControl fullWidth>
                        <InputLabel>Servicio</InputLabel>
                        <Select
                          label="Servicio"
                          variant="outlined"
                          fullWidth
                          value={row.selectedServicio}
                          onChange={(e) =>
                            handleSelectChange(index, "selectedServicio", e.target.value)
                          }
                          sx={{ p: 1.5 }}
                          placeholder="Seleccione.."
                        >
                          <MenuItem value="">Ninguna</MenuItem>
                          {getServicioOptions(index).map((servicio) => (
                            <MenuItem key={servicio.idServicio} value={servicio.idServicio}>
                              {servicio.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Tooltip
                        title="Si un rostro se encuentra aqui, habilita el acceso."
                        arrow
                        placement="top"
                      >
                        <FormControl fullWidth>
                          <InputLabel>Watchlist Principal</InputLabel>
                          <Select
                            label="Watchlist Principal"
                            variant="outlined"
                            fullWidth
                            value={row.selectedWatchlistClientes}
                            onChange={(e) =>
                              handleSelectChange(index, "selectedWatchlistClientes", e.target.value)
                            }
                            sx={{ p: 1.5 }}
                            placeholder="Seleccione.."
                          >
                            <MenuItem value="">Ninguna</MenuItem>
                            {watchlist?.map((item) => (
                              <MenuItem key={item.watchlist_id} value={item.watchlist_id}>
                                {item.display_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton color="error" onClick={() => handleRemoveRow(index)}>
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <MDBox textAlign="center" mt={3}>
                  <MDButton
                    onClick={handleAddRow}
                    variant="gradient"
                    color="info"
                    disabled={disableAdd}
                  >
                    Nuevo Servicio
                  </MDButton>
                  <MDButton onClick={handleSave} variant="gradient" color="success" sx={{ ml: 2 }}>
                    Guardar
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default AddServiciosSucursal
